import axios from 'axios';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { BACKEND_SERVER_URL } from '../../../http/http-config';
import { sortInAlphabeticalOrder } from '../common-helpers/commonServices';

const CUET_TestManagementModal = forwardRef((props, ref) => {
    const [modalBox, setModalBox] = useState(false);
    const [topics, setTopics] = useState([]);
    const [examType, setExamType] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [assignedQuestions, setAssignedQuestions] = useState(0);
    const [questionLimit, setQuestionLimit] = useState(0);
    const [topicWiseSubjects, setTopicsWiseSubjects] = useState([]);
    const [combinedGATopics, setCombinedGATopics] = useState([]);
    const [selectedExam, setSelectedExam] = useState("Section-III");
    const [syncChanges, setSyncChanges] = useState(false);


    const [subjectWiseTopics, setSubjectWiseTopics] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState({ SubjectType: "All SUBJECT NAME" });
    const [filteredSections, setFilteredSections] = useState([]);
    const [selectedSection, setSelectedSection] = useState({ Section: "All TOPICS" });
    const [selectedTopic, setSelectedTopic] = useState({ Topic: "All SUB TOPIC" });
    const [displayTopics, setDisplayTopics] = useState([]);


    useImperativeHandle(ref, () => ({
        openModal(item) {
            setExamType(item);
            setModalBox(true);
        }
    }));

    useEffect(() => {
        if (!examType || !modalBox) return
        const response = axios.all([
            axios.get(BACKEND_SERVER_URL + '/cuet/topics/' + examType),
            axios.get(BACKEND_SERVER_URL + '/cuet/quetion-limit/' + examType),
        ]);
        response.then(axios.spread((topicResponse, questionLimitResponse) => {
            let topics = topicResponse?.data?.topics;
            setAssignedQuestions(
                (topics ?? [])?.reduce((acc, curr) => acc + (curr?.NoOfQuestion ?? 0), 0)
            );
            let questionLimit = questionLimitResponse?.data?.questionLimit
            setTopics(sortInAlphabeticalOrder(topics, 'Subject'));
            setQuestionLimit(questionLimit);

        })).catch((error) => {
            console.log(error);
            toast.error(error.response?.data?.message || "An error occurred while fetching subjects and sections.");
        });
        return () => {
            setExamType('');
            setAssignedQuestions(0);
            setTopics([]);
            setSubjects([]);
            setSelectedSubject({ SubjectType: "All SUBJECT NAME" });
            setSelectedSection({ Section: "All TOPICS" });
            setSelectedTopic({ Topic: "All SUB TOPIC" });
        };

    }, [modalBox, examType])

    useEffect(() => {
        if ((examType == 'Section-II' || examType == 'Section-IA') && topics.length) {
            axios.get(BACKEND_SERVER_URL + '/cuet/subjects/' + examType).then((res) => {
                let subjects = res.data.subjects?.filter(data => topics.map(data2 => data2.SubjectTypeID).includes(data.Sn));
                setSubjects(subjects);
                setSelectedSubject({ SubjectType: "All SUBJECT NAME" });
            }).catch((error) => {
                console.log(error);
                toast.error(error.response?.data?.message || "An error occurred while fetching subjects.");
            }
            )
        } else if (examType == 'Section-III' && topics.length) {
            // For Section III, subjects might not be explicit, but we should still handle initialization
            setSelectedSubject({ SubjectType: "All SUBJECT NAME" });
        }
    }, [topics.length, examType])

    useEffect(() => {
        if (!topics.length) return;

        let filtered = [...topics];

        // Level 2: Subject Filter
        if (selectedSubject?.Sn) {
            filtered = filtered.filter(t => t.SubjectTypeID === selectedSubject.Sn);
        }

        // Level 3: Section Filter (Available sections based on current filtered list)
        const uniqueSections = [];
        const sectionMap = new Map();
        filtered.forEach(t => {
            if (!sectionMap.has(t.SectionID)) {
                sectionMap.set(t.SectionID, { Sn: t.SectionID, Section: t.Section });
                uniqueSections.push({ Sn: t.SectionID, Section: t.Section });
            }
        });
        setFilteredSections(sortInAlphabeticalOrder(uniqueSections, 'Section'));

        if (selectedSection?.Sn) {
            filtered = filtered.filter(t => t.SectionID === selectedSection.Sn);
        }

        // Level 4: Topic Filter (Available topics based on current filtered list)
        if (selectedTopic?.Sn) {
            filtered = filtered.filter(t => t.Sn === selectedTopic.Sn);
        }

        setDisplayTopics(filtered);

        // Update assigned questions count for the visible rows or total? 
        // Usually, total assigned questions is for the whole exam type.
        // But for display consistency, we might want to recalculate based on topics being edited.
    }, [selectedSubject, selectedSection, selectedTopic, topics, syncChanges])

    const handleSubjectChange = (subject) => {
        setSelectedSubject(subject || { SubjectType: "All SUBJECT NAME" });
        setSelectedSection({ Section: "All TOPICS" });
        setSelectedTopic({ Topic: "All SUB TOPIC" });
    }

    const handleSectionChange = (section) => {
        setSelectedSection(section || { Section: "All TOPICS" });
        setSelectedTopic({ Topic: "All SUB TOPIC" });
    }

    const handleTopicChange = (topic) => {
        setSelectedTopic(topic || { Topic: "All SUB TOPIC" });
    }

    const handleSaveTopic = async () => {
        try {
            let topicsToBeUpdated;
            if (examType !== 'COMBINED') {
                topicsToBeUpdated = examType == 'Section-II' ? topicWiseSubjects?.flatMap(subject => subject.topics?.filter(topic => topic.isUpdated)) : topics?.filter(topic => topic.isUpdated);
            } else {
                topicsToBeUpdated = [...topics?.filter(topic => topic.isUpdated)];
            }
            if (topicsToBeUpdated.length == 0) {
                toast.info("No changes made to update");
                setModalBox(false);
                return;
            }

            const response = await axios.post(
                BACKEND_SERVER_URL + '/cuet/updateTestManagement/', topicsToBeUpdated?.map(data => ({ Sn: data.Sn, NoOfQuestion: data.NoOfQuestion, isUpdated: data.isUpdated || false }))
            );

            toast.success(response.data.message);
            props.setSubjectLoading(!props.loadSubjects);
            setModalBox(false);
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.message || "Something went wrong");
        }
    };


    const handleQCountChange = (Sn, value, SubjectID) => {
        console.log(SubjectID, Sn, value);
        value = Number(value);

        let copy = [...topics];
        let selectedToic = copy.find((topic, index) => topic.Sn == Sn);
        let preNumber = Number(selectedToic?.NoOfQuestion);
        if (value > preNumber) {
            setAssignedQuestions(prev => prev + (value - preNumber));
        } else if (value < preNumber) {
            setAssignedQuestions(prev => prev - (preNumber - value));
        }
        selectedToic.NoOfQuestion = value;
        selectedToic.isUpdated = true;
        setTopics(sortInAlphabeticalOrder(copy, 'Subject'));
        setSyncChanges(!syncChanges);
    }

    return (
        <Modal
            onHide={() => setModalBox(false)}
            show={modalBox}
            size="xl"
            backdrop="static"
            keyboard={false}
        >
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">
                        Test Management
                    </h5>
                    <button type="button" className="btn-close" onClick={() => setModalBox(false)}></button>
                </div>

                <div className=" px-4 pb-0">
                    <div className="d-flex w-100 justify-content-between align-items-center mt-0 pt-0 gap-2 mb-3">
                        <span className={`p-1 px-3 fw-bold text-white bg-primary fs-5 border border-2 rounded text-nowrap`} style={{ minWidth: 'fit-content' }}>
                            {examType === "Section-IA" ? "Language Subjects" : examType === "Section-II" ? "Domain-Specific Subjects" : examType === "Section-III" ? "General Aptitude Test" : examType}
                        </span>

                        <div className="d-flex flex-grow-1 gap-2 mx-3 justify-content-center">
                            {(examType == 'Section-II' || examType == 'Section-IA') && (
                                <div style={{ width: '200px' }}>
                                    <Select
                                        value={selectedSubject?.Sn ? { value: selectedSubject, label: selectedSubject.SubjectType } : { value: { SubjectType: "All SUBJECT NAME" }, label: "All SUBJECT NAME" }}
                                        onChange={(opt) => handleSubjectChange(opt.value)}
                                        options={[{ Sn: 0, SubjectType: "All SUBJECT NAME" }, ...subjects]?.map(s => ({ value: s, label: s.SubjectType }))}
                                        styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8' }) }}
                                        placeholder="Select SUBJECT NAME"
                                    />
                                </div>
                            )}

                            <div style={{ width: '200px' }}>
                                <Select
                                    value={selectedSection?.Sn ? { value: selectedSection, label: selectedSection.Section } : { value: { Section: "All TOPICS" }, label: "All TOPICS" }}
                                    onChange={(opt) => handleSectionChange(opt.value)}
                                    options={[{ Sn: 0, Section: "All TOPICS" }, ...filteredSections]?.map(s => ({ value: s, label: s.Section }))}
                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8' }) }}
                                    placeholder="Select TOPICS"
                                />
                            </div>

                            <div style={{ width: '200px' }}>
                                <Select
                                    value={selectedTopic?.Sn ? { value: selectedTopic, label: selectedTopic.Topic } : { value: { Topic: "All SUB TOPIC" }, label: "All SUB TOPIC" }}
                                    onChange={(opt) => handleTopicChange(opt.value)}
                                    options={[{ Sn: 0, Topic: "All SUB TOPIC" }, ...displayTopics]?.map(s => ({ value: s, label: s.Topic }))}
                                    styles={{ control: (provided) => ({ ...provided, borderColor: '#17a2b8' }) }}
                                    placeholder="Select SUB TOPIC"
                                />
                            </div>
                        </div>

                        <span className={`p-1 px-3 fw-bold text-white ${assignedQuestions == questionLimit ? 'bg-success' : 'bg-danger'} fs-5 border border-2 rounded text-nowrap`}>
                            Total Qs: {assignedQuestions} / {questionLimit}
                        </span>
                    </div>
                    <div className="d-flex w-100 justify-content-around mb-3">
                        <div className="mb-3  col-12">
                            <div className="table-responsive full-data" style={{ paddingBottom: "1px", transform: 'rotateX(180deg)' }}>
                                <div id="example-student_wrapper" className="dataTables_wrapper no-footer">
                                    <table className="table-responsive-md table display dataTablesCard student-tab 
                                                                dataTable no-footer mt-0 mb-0" style={{ transform: 'rotateX(180deg)' }} id="example-student" >
                                        <thead style={{ backgroundColor: "#cccccc" }} >
                                            <tr className='text-center  border border-left'>
                                                {/* <th className=' custom-table-header p-2'>SN</th> */}
                                                <th className=' custom-table-header p-2 text-start'>SUBJECT NAME</th>
                                                <th className=' custom-table-header p-2 text-start'>TOPICS</th>
                                                <th className=' custom-table-header p-2  ps-3'>SUB TOPIC</th>
                                                <th className=' custom-table-header p-2  ps-3'>ASSIGNED Qs.</th>
                                                <th className=' custom-table-header p-2  ps-3'>AVAILABLE Qs.</th>

                                            </tr>
                                        </thead>
                                        <tbody>
                                            {displayTopics?.map((item, ind) => (
                                                <tr key={ind} className={`${ind % 2 == 1 ? 'custom row-height' : 'row-height'}`} >


                                                    <td className='p-1' style={{ border: "1px solid #cccccc" }}>
                                                        <div className="d-flex justify-content-between ">
                                                            <h6 className='mb-0 ps-3' style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.SubjectType}

                                                            </h6>
                                                        </div>
                                                    </td>
                                                    <td className='p-1' style={{ border: "1px solid #cccccc" }}>
                                                        <div className="d-flex justify-content-between ">
                                                            <h6 className='mb-0 ps-3' style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.Section}

                                                            </h6>
                                                        </div>
                                                    </td>

                                                    <td className='p-1 text-start' style={{ border: "1px solid #cccccc" }}>
                                                        <h6 className="mb-0 ">
                                                            {item.Topic}
                                                        </h6>
                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "65px" }}>
                                                        <input type="number" name={`${ind}questionCount`} className="form-control p-0 px-2" style={{ height: '1.75rem', lineHeight: '1.75rem', fontSize: '0.875rem' }} value={item?.NoOfQuestion ?? 0} min={0} onChange={(e) => handleQCountChange(item.Sn, e.target.value)} />

                                                    </td>
                                                    <td className='p-1 text-center' style={{ border: "1px solid #cccccc", width: "65px" }}>
                                                        <h6 className="mb-0 ">
                                                            {item.AvailableQuestions ?? 0}
                                                        </h6>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                        </div>
                    </div>

                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-danger btn-xs" onClick={() => setModalBox(false)}>Close</button>
                    <button type="button" className="btn btn-primary btn-xs" onClick={handleSaveTopic}>
                        Update
                    </button>
                </div>
            </div>
        </Modal>
    );
});

export default CUET_TestManagementModal;
